/**
 * #360: a phone number is entered as a country code plus the national number without its
 * leading zero ("+46" and "733 12 34 56") and is sent to Diflex joined ("+46 733 12 34 56").
 * The code is pre-filled from the chosen Country and can be changed for a foreign number.
 */

// Keyed on Country as it appears on the customer's users — Entra's free text, or the English
// region name the LDAP sync writes — plus the Swedish name and the ISO code of each country.
const DIAL_CODES = {
  sweden: "+46",
  sverige: "+46",
  se: "+46",
  norway: "+47",
  norge: "+47",
  no: "+47",
  denmark: "+45",
  danmark: "+45",
  dk: "+45",
  finland: "+358",
  fi: "+358",
  iceland: "+354",
  island: "+354",
  is: "+354",
  germany: "+49",
  tyskland: "+49",
  de: "+49",
  "united kingdom": "+44",
  storbritannien: "+44",
  uk: "+44",
  gb: "+44",
  "united states": "+1",
  usa: "+1",
  us: "+1",
};

/** Used for an empty or unlisted Country: Diflex's customers are Swedish. */
export const DEFAULT_DIAL_CODE = "+46";

/**
 * @param {string} country The Country chosen in the form
 * @returns {string} The country's calling code, e.g. "+46"
 */
export const dialCodeForCountry = (country) =>
  DIAL_CODES[(country || "").trim().toLowerCase()] || DEFAULT_DIAL_CODE;

/**
 * The code in use: the orderer's own entry once there is one, otherwise the Country's.
 *
 * @param {string|undefined} override What the orderer typed in the code field, if anything
 * @param {string} country The Country chosen in the form
 * @returns {string}
 */
export const effectiveDialCode = (override, country) => override ?? dialCodeForCountry(country);

/**
 * Validation message for a number and its country code, or null. An empty number is fine —
 * both phone fields are optional.
 *
 * @param {string} number The national number as typed
 * @param {string} dialCode The country code in use
 * @returns {string|null}
 */
export const phoneNumberError = (number, dialCode) => {
  const trimmed = (number || "").trim();
  if (!trimmed) return null;

  if (!/^\+\d{1,4}$/.test((dialCode || "").trim())) {
    return "Enter the country code as + followed by digits, e.g. +46.";
  }

  if (/^[0+]/.test(trimmed)) {
    return "Enter the number without a leading 0 or country code, e.g. 733 12 34 56.";
  }

  return null;
};

/**
 * @param {string} dialCode The country code in use
 * @param {string} number The national number as typed
 * @returns {string} "+46 733 12 34 56", or "" when no number was entered
 */
export const withDialCode = (dialCode, number) => {
  const trimmed = (number || "").trim();
  return trimmed ? `${(dialCode || "").trim()} ${trimmed}` : "";
};
